(() => {
  const callback = async () => {
    const YOTPO_APP_KEY = 'HuIgnH7XiPv6ba8rxZYGEhWKWx7NAIJSKqu7Rt5d';
    const REVIEWS_PER_PAGE = 5;

    const switchPopupState = ({ state }) => {
      const stateElements = [popupForm, popupStateSuccess, popupStateError].filter(Boolean);
      stateElements.forEach((element) => toggleClass({ element, className: 'hidden', value: element != state }));
    };

    const switchPhotoPreviewState = ({ state }) => {
      const stateClassNames = ['processing', 'loaded', 'error'];
      stateClassNames.forEach((className) => {
        toggleClass({ element: popupPhotoPreview, className, value: className == state });
      });
    };

    const handleFormSubmit = async ({ productSku, productTitle, productUrl }) => {
      toggleClass({ element: popupSubmitButton, className: 'btn--loading', value: true });

      const productData = {
        sku: productSku,
        product_title: productTitle,
        product_url: productUrl,
      };

      const form = new FormData(popupForm);
      const fields = Object.fromEntries(form.entries());
      const formData = mapFormFields({ fields });

      await createReview({ productData, formData });

      toggleClass({ element: popupSubmitButton, className: 'btn--loading', value: false });
    };

    const mapFormFields = ({ fields }) => {
      const { name, email, score, title, content, photoUrl, sizing, sizeBought, sizeBra } = fields ?? {};

      const customFields = {
        '--67843': sizing,
        '--100928': sizeBought,
        '--100929': sizeBra,
      };

      return {
        display_name: name,
        email,
        review_content: content,
        review_title: title,
        review_score: score,
        custom_fields: customFields,
        image_urls: [photoUrl],
      };
    };

    const createReview = async ({ productData, formData }) => {
      const options = {
        method: 'POST',
        headers: { accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appkey: YOTPO_APP_KEY,
          ...productData,
          ...formData,
        }),
      };

      try {
        const request = await fetch('https://api.yotpo.com/reviews/dynamic_create', options);

        const state = request.ok ? popupStateSuccess : popupStateError;
        switchPopupState({ state });
      } catch (error) {
        switchPopupState({ state: popupStateError });
        console.error(error);
      }
    };

    const fetchSummary = async ({ productId }) => {
      if (!productId) return;

      try {
        const request = await fetch(
          `https://api-cdn.yotpo.com/v1/widget/${YOTPO_APP_KEY}/products/${productId}/reviews.json?per_page=1`,
          {
            method: 'GET',
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          },
        );

        const data = await request.json();
        const { bottomline } = data?.response ?? {};

        return mapSummary(bottomline);
      } catch (error) {
        console.error(error);
      }
    };

    const mapSummary = (bottomline) => {
      if (!bottomline) return;

      const {
        average_score: averageScore,
        total_review: totalReviews,
        custom_fields_bottomline: customFields,
      } = bottomline;

      const formattedScore = averageScore?.toFixed(1);

      const sizingAverageValue = customFields?.['--67843']?.['average_score'];

      const sizingValues = {
        1: {
          locale: locale.small,
          position: 'start',
        },
        2: {
          locale: locale.trueToSize,
          position: 'center',
        },
        3: {
          locale: locale.big,
          position: 'end',
        },
      };

      return {
        score: formattedScore,
        starsHtml: getStarsHtml(formattedScore),
        totalReviews,
        sizing: sizingValues[sizingAverageValue],
      };
    };

    const fetchReviews = async ({
      productId,
      page = 1,
      sortType = 'date',
      sortDirection = 'desc',
      withPictureOnly = false,
      sizeValue,
    }) => {
      if (!productId) return;

      const sizeFilter = sizeValue
        ? [
            {
              question_id: 100928,
              answers: [sizeValue],
            },
          ]
        : [];

      const pictureFilter = withPictureOnly ? { pictured: withPictureOnly } : {};

      const options = {
        domain_key: productId,
        per_page: REVIEWS_PER_PAGE,
        page: page,
        crfs: [...sizeFilter],
        sortings: [
          {
            sort_by: sortType,
            ascending: sortDirection === 'asc',
          },
        ],
        ...pictureFilter,
      };

      try {
        const request = await fetch(`https://api-cdn.yotpo.com/v1/reviews/${YOTPO_APP_KEY}/filter.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=utf-8',
          },
          body: JSON.stringify(options),
        });

        const data = await request.json();
        const { reviews, pagination } = data?.response ?? {};

        const { total: totalReviews, per_page: reviewsPerPage, page } = pagination ?? {};

        const totalPages = Math.ceil(totalReviews / reviewsPerPage);
        const isLastPage = page >= totalPages;

        return {
          reviews: reviews?.map(mapReview) || [],
          pagination: {
            page,
            reviewsPerPage,
            totalReviews,
            totalPages,
            isLastPage,
          },
        };
      } catch (error) {
        console.error(error);
      }
    };

    const mapReview = (review) => {
      // Date formatting (dd/mm/yy)
      const date = new Date(review.created_at);

      let day = date.getDate();
      let month = date.getMonth() + 1;
      let year = date.getFullYear();

      day = day.toString().padStart(2, '0');
      month = month.toString().padStart(2, '0');
      year = year.toString().slice(-2);

      const formattedDate = `${day}/${month}/${year}`;

      // Custom fields
      const customFields = Object.entries(review.custom_fields ?? {}).map(([key, field]) => {
        const { title, value } = field;

        return { key, title, value };
      });

      return {
        id: review.id,
        date: formattedDate,
        customerName: review.user.display_name,
        verifiedBuyer: review.verified_buyer,
        score: review.score,
        title: review.title,
        content: review.content,
        customFields,
        images: review.images_data ?? [],
      };
    };

    const getStarsHtml = (score) => {
      if (!score) return;

      const filledStar =
        '<svg class="icon--star-filled" width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m8 0 1.796 5.528h5.813l-4.703 3.416 1.796 5.528L8 11.056l-4.702 3.416 1.796-5.528L.392 5.528h5.812L8 0Z" fill="#121212"/></svg>';
      const emptyStar =
        '<svg class="icon--star-empty" width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m8 1.352 1.399 4.305.094.289h4.83l-3.662 2.66-.246.179.094.288 1.399 4.305-3.662-2.66L8 10.539l-.246.179-3.662 2.66 1.4-4.305.093-.288-.246-.179-3.662-2.66h4.83l.094-.289L8 1.352Z" stroke="#121212" stroke-width=".836"/></svg>';
      const halfStar =
        '<svg class="icon--star-half" width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 0V11.056l-4.702 3.416 1.796-5.528L.392 5.528h5.812L8 0Z" fill="#121212"/><path d="m8 1.352 1.399 4.305.094.289h4.83l-3.662 2.66-.246.179.094.288 1.399 4.305-3.662-2.66L8 10.539l-.246.179-3.662 2.66 1.4-4.305.093-.288-.246-.179-3.662-2.66h4.83l.094-.289L8 1.352Z" stroke="#121212" stroke-width=".836"/></svg>';

      const starsHtml = [...Array(5)]
        .map((_, idx) => {
          const value = score - idx;

          if (value >= 0.5 && value < 1) return halfStar;
          if (value >= 1) return filledStar;
          return emptyStar;
        })
        .join('');

      return starsHtml;
    };

    const getCustomFieldsHtml = (customFields) => {
      if (!customFields?.length) return;

      const fieldsLocalization = {
        '--67843': {
          title: locale.sizing,
          1: locale.small,
          2: locale.trueToSize,
          3: locale.big,
        },
        '--100928': {
          title: locale.sizeBought,
        },
        '--100929': {
          title: locale.braSize,
        },
      };

      const customFieldsHtml = customFields
        .map((field) => {
          const { key, title, value } = field;

          // Look for localized text with fallback to default text that comes from Yotpo
          const fieldTitle = fieldsLocalization[key]?.title || title;
          const fieldValue = fieldsLocalization[key]?.[value] || value;

          return `<p>
                  <span>${fieldTitle}</span>
                  <span>${fieldValue}</span>
                </p>`;
        })
        .join('');

      return customFieldsHtml;
    };

    const createElement = (review) => {
      if (!review) return;

      const starsHtml = getStarsHtml(review.score);
      const customFieldsHtml = getCustomFieldsHtml(review.customFields);

      const verifiedBuyer = !!review.verifiedBuyer
        ? `<p class="product-reviews__review-verified">
            <span>${locale.verifiedBuyer}</span>
            <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18.33a8.333 8.333 0 1 0 0-16.666 8.333 8.333 0 0 0 0 16.667Z" stroke="#121212"/><path d="m6.667 10.414 2.5 2.5 4.167-5" stroke="#121212" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </p>`
        : '';

      const customFields = !!customFieldsHtml
        ? `<div class="product-reviews__review-fields">
            ${customFieldsHtml}
          </div>`
        : '';

      const images = !!review.images.length
        ? `<div class="product-reviews__review-image">
            ${review.images.map((image) => `<img src="${image.original_url}" alt="${locale.attachedPhoto}">`)}
          </div>`
        : '';

      const element = document.createElement('div');
      element.classList = 'product-reviews__review';
      element.innerHTML = `<div class="product-reviews__review-person">
                          <div class="product-reviews__review-heading">
                            <p class="product-reviews__review-name">
                              ${review.customerName}
                            </p>
                            
                            ${verifiedBuyer}
                          </div>

                          <p class="product-reviews__review-date">${review.date}</p>
                        
                          ${customFields}

                          ${images}
                        </div>

                        <div class="product-reviews__review-details">
                          <p class="product-reviews__review-score">
                            ${starsHtml}
                          </p>

                          <p class="product-reviews__review-title">
                            ${review.title}
                          </p>

                          <div class="product-reviews__review-content">
                            <p class="product-reviews__review-text">
                              ${review.content}
                            </p>

                            <p class="product-reviews__review-toggler product-reviews__review-toggler--more">
                              ${locale.readMore}
                            </p>

                            <p class="product-reviews__review-toggler product-reviews__review-toggler--less">
                              ${locale.readLess}
                            </p>
                          </div>

                          ${customFields}

                          ${images}
                        </div>`;

      element.addEventListener('click', (event) => {
        // Collapse review content by click on it
        event.currentTarget.classList.toggle('collapsed');
      });

      return element;
    };

    const getSelectedOptions = () => {
      // Sort
      const { value: sortType, options: sortOptions, selectedIndex: sortIndex } = sortingSelector ?? {};
      const sortDirection = sortOptions?.[sortIndex]?.dataset?.direction;

      // Filters
      const { value: sizeValue } = filterSizeSelector ?? {};
      const withPictureOnly = withPictureToggler?.checked;

      return { sortType, sortDirection, withPictureOnly, sizeValue };
    };

    const collapseReviews = () => {
      const reviews = document.querySelectorAll(selectors.review);

      reviews.forEach((item) => {
        const content = item.querySelector(selectors.reviewText);
        const isInitialized = item.classList.contains('can-collapse');

        // Collapse content and show toggler if content takes
        // more than lines height
        const LINES_HEIGHT = 40; // (2 lines x 20px line-height)
        if (content?.scrollHeight > LINES_HEIGHT) {
          // Guard to prevent re-collapsing of already initialized reviews
          if (isInitialized) return;

          item.classList.add('collapsed', 'can-collapse');
        } else {
          item.classList.remove('collapsed', 'can-collapse');
        }
      });
    };
    // Recheck content height on window resize
    window.addEventListener('resize', collapseReviews);

    const toggleClass = ({ element, className, value }) => {
      if (!element) return;

      element.classList.toggle(className, value);
    };

    const setInnerHtml = ({ element, content }) => {
      if (!element || !content) return;

      element.innerHTML = content;
    };

    const toggleLoadingState = (isLoading) => {
      toggleClass({ element: reviewsContainer, className: 'processing', value: isLoading });
      toggleClass({ element: loadMoreButton, className: 'btn--loading', value: isLoading });
    };

    const loadReviews = async ({ productId, page, override = false }) => {
      if (!reviewsContainer) return;

      toggleLoadingState(true);

      const { sortType, sortDirection, withPictureOnly, sizeValue } = getSelectedOptions();

      const data = await fetchReviews({
        productId,
        page,
        sortType,
        sortDirection,
        withPictureOnly,
        sizeValue,
      });

      const { reviews, pagination } = data ?? {};

      if (!!reviews?.length) {
        const elements = reviews.map(createElement);

        if (override) {
          reviewsContainer.replaceChildren(...elements);
        } else {
          reviewsContainer.append(...elements);
        }

        toggleClass({ element: loadMoreButton, className: 'hidden', value: pagination.isLastPage });

        collapseReviews();
      } else {
        let content;

        const hasAppliedFilters = sizeValue || withPictureOnly;
        if (!hasAppliedFilters) {
          content = `<div class="product-reviews__empty-state product-reviews__empty-state--no-reviews">
                      <div class="product-reviews__empty-state-icon">
                        <svg width="60" height="60" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M400 15.625C200.704 15.625 39.1566 177.172 39.1566 376.469C39.05 449.878 61.4345 521.557 103.297 581.859L86.2191 784.375L296.329 722.172C329.965 732.234 364.891 737.335 400 737.313C599.282 737.313 760.844 575.75 760.844 376.469C760.844 177.188 599.282 15.625 400 15.625Z" stroke="black" stroke-width="31.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M400 160.328L470.234 302.625L627.266 325.437L513.641 436.203L540.453 592.609L400 518.766L259.531 592.609L286.359 436.203L172.734 325.437L329.766 302.625L400 160.328Z" stroke="black" stroke-width="31.25" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </div>
                      <div class="product-reviews__empty-state-group">
                        <p class="product-reviews__empty-state-title">${locale.whatYouThink}</p>
                        <p class="product-reviews__empty-state-subtitle">${locale.beFirst}</p>
                      </div>
                      <button type="button" class="popup__trigger btn btn--small" data-trigger-for="product-reviews-ds">${locale.writeReview}</button>
                    </div>`;
        } else {
          content = `<div class="product-reviews__empty-state product-reviews__empty-state--not-found">
                      <div class="product-reviews__empty-state-icon">
                        <svg width="60" height="60" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m580.422 464.156-31.375 62.735H250.953l-31.375-62.766H62.688a47.072 47.072 0 0 0-33.302 13.816 47.09 47.09 0 0 0-13.761 33.325V699.5a47.08 47.08 0 0 0 47.063 47.062h674.625a47.076 47.076 0 0 0 47.062-47.062V511.266a47.078 47.078 0 0 0-47.062-47.078l-156.891-.032Z" stroke="#000" stroke-width="31.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M564.734 605.359H235.266c-17.328 0-31.375 14.047-31.375 31.375s14.047 31.375 31.375 31.375h329.468c17.328 0 31.375-14.047 31.375-31.375s-14.047-31.375-31.375-31.375ZM596.109 87.625h70.938a47.08 47.08 0 0 1 46.219 38.188l71.109 369.718M15.625 495.531l71.094-369.718a47.062 47.062 0 0 1 39.062-37.626l78.125-.156M109.75 464.156v-65.625a47.064 47.064 0 0 1 47.078-47.062h486.344a47.068 47.068 0 0 1 33.288 13.779 47.053 47.053 0 0 1 13.79 33.283v65.625" stroke="#000" stroke-width="31.25" stroke-linecap="round" stroke-linejoin="round"/><path d="M141.141 351.547V299.25a47.05 47.05 0 0 1 13.778-33.288 47.074 47.074 0 0 1 33.284-13.79h423.594a47.065 47.065 0 0 1 43.484 29.063 47.05 47.05 0 0 1 3.578 18.015v52.297M172.516 252.172v-52.297a47.066 47.066 0 0 1 13.784-33.278 47.061 47.061 0 0 1 33.278-13.784h360.844a47.047 47.047 0 0 1 33.278 13.784 47.06 47.06 0 0 1 13.784 33.278v52.297M203.891 152.812v-52.296a47.077 47.077 0 0 1 47.062-47.063h298.094a47.078 47.078 0 0 1 47.062 47.063v52.296" stroke="#000" stroke-width="31.25" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </div>
                      <p class="product-reviews__empty-state-subtitle">${locale.noReviewsFound}</p>
                    </div>`;
        }

        setInnerHtml({
          element: reviewsContainer,
          content,
        });

        document.dispatchEvent(new CustomEvent('popup:init'));

        toggleClass({ element: loadMoreButton, className: 'hidden', value: true });
      }

      toggleLoadingState(false);
    };

    const loadSummary = async ({ productId }) => {
      const data = await fetchSummary({ productId });

      const { score, starsHtml, totalReviews, sizing } = data ?? {};
      const { locale: sizingLocale, position: sizingPosition } = sizing ?? {};

      if (totalReviews === 0) {
        toggleClass({ element: summary, className: 'hidden', value: true });
        return;
      }

      const hasRatingData = score && starsHtml && totalReviews;
      toggleClass({ element: summaryRatingWrapper, className: 'hidden', value: !hasRatingData });

      if (hasRatingData) {
        setInnerHtml({ element: summaryScore, content: score });
        setInnerHtml({ element: summaryStars, content: starsHtml });
        setInnerHtml({
          element: summaryTotalReviews,
          content: locale.basedOnReviews?.replace('[number]', totalReviews),
        });
      }

      const hasSizingData = sizingLocale && sizingPosition;
      toggleClass({ element: summarySizingWrapper, className: 'hidden', value: !hasSizingData });

      if (hasSizingData) {
        setInnerHtml({ element: summarySizing, content: sizingLocale });
        setInnerHtml({ element: summarySizingTitle, content: `${locale.sizing}:` });
        setInnerHtml({
          element: summaryProgressBar,
          content: '<span class="product-reviews__progress-bar-point"></span>',
        });
        if (summaryProgressBar) summaryProgressBar.style.alignItems = sizingPosition;
      }
    };

    const selectors = {
      section: '.product-reviews:not(.initialized)',
      reviewsContainer: '.product-reviews__reviews-container',
      loadMoreButton: '.product-reviews__load-more',
      sortingSelector: '.product-reviews__sort select',
      withPictureToggler: '.product-reviews__filter-picture input',
      filterSizeSelector: '.product-reviews__filter-size select',
      review: '.product-reviews__review',
      reviewText: '.product-reviews__review-text',
      summary: '.product-reviews__summary',
      summaryRatingWrapper: '.product-reviews__summary-rating',
      summaryScore: '.product-reviews__average-score',
      summaryStars: '.product-reviews__average-stars',
      summaryTotalReviews: '.product-reviews__total-reviews',
      summarySizingWrapper: '.product-reviews__summary-sizing',
      summarySizingTitle: '.product-reviews__average-sizing-title',
      summarySizing: '.product-reviews__average-sizing',
      summaryProgressBar: '.product-reviews__progress-bar',
      popupForm: '.product-reviews__popup-form',
      popupSubmitButton: '.product-reviews__popup-form button[type="submit"]',
      popupStateSuccess: '.product-reviews__popup-status--successful',
      popupStateError: '.product-reviews__popup-status--error',
      popupBackToFormButton: '.product-reviews__popup-back-button',
      popupPhotoUrlInput: 'input#product-reviews-photo-url',
      popupPhotoPreview: '.product-reviews__popup-photo-preview',
      popupPhotoPreviewImage: '.product-reviews__popup-photo-preview-image',
      popupPhotoPreviewDeleteButton: '.product-reviews__popup-photo-preview-delete-button',
    };

    const section = document.querySelector(selectors.section);
    if (!section) return;

    const reviewsContainer = section.querySelector(selectors.reviewsContainer);
    const loadMoreButton = section.querySelector(selectors.loadMoreButton);
    const sortingSelector = section.querySelector(selectors.sortingSelector);
    const withPictureToggler = section.querySelector(selectors.withPictureToggler);
    const filterSizeSelector = section.querySelector(selectors.filterSizeSelector);
    const summary = section.querySelector(selectors.summary);
    const summaryRatingWrapper = section.querySelector(selectors.summaryRatingWrapper);
    const summaryScore = section.querySelector(selectors.summaryScore);
    const summaryStars = section.querySelector(selectors.summaryStars);
    const summaryTotalReviews = section.querySelector(selectors.summaryTotalReviews);
    const summarySizingWrapper = section.querySelector(selectors.summarySizingWrapper);
    const summarySizingTitle = section.querySelector(selectors.summarySizingTitle);
    const summarySizing = section.querySelector(selectors.summarySizing);
    const summaryProgressBar = section.querySelector(selectors.summaryProgressBar);
    const popupForm = section.querySelector(selectors.popupForm);
    const popupSubmitButton = section.querySelector(selectors.popupSubmitButton);
    const popupStateSuccess = section.querySelector(selectors.popupStateSuccess);
    const popupStateError = section.querySelector(selectors.popupStateError);
    const popupBackToFormButtons = section.querySelectorAll(selectors.popupBackToFormButton);
    const popupPhotoUrlInput = section.querySelector(selectors.popupPhotoUrlInput);
    const popupPhotoPreview = section.querySelector(selectors.popupPhotoPreview);
    const popupPhotoPreviewImage = section.querySelector(selectors.popupPhotoPreviewImage);
    const popupPhotoPreviewDeleteButton = section.querySelector(selectors.popupPhotoPreviewDeleteButton);

    const { productId, productSku, productTitle, productUrl, locale } =
      window.themeComponents.snippets['product-reviews-ds'] ?? {};

    if (!productId || !locale) {
      section.remove();
      return;
    }

    loadSummary({ productId });

    let currentPage = 1;
    loadReviews({
      productId,
      page: 1,
      override: true,
    });

    toggleClass({ element: section, className: 'initialized', value: true });

    // Pagination
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', () => loadReviews({ productId, page: ++currentPage, override: false }));
    }

    // Sorting and filtering
    [sortingSelector, withPictureToggler, filterSizeSelector].filter(Boolean).forEach((element) => {
      element.addEventListener('change', () =>
        loadReviews({
          productId,
          page: 1,
          override: true,
        }),
      );
    });

    // Write a review form
    if (popupForm) {
      popupForm.addEventListener('submit', (event) => {
        event.preventDefault();
        handleFormSubmit({ productSku, productTitle, productUrl });
      });

      popupBackToFormButtons.forEach((button) => {
        button.addEventListener('click', () => switchPopupState({ state: popupForm }));
      });

      // Photo preview
      if (popupPhotoPreview && popupPhotoUrlInput && popupPhotoPreviewImage && popupPhotoPreviewDeleteButton) {
        popupPhotoPreviewImage.addEventListener('load', () => {
          switchPhotoPreviewState({ state: 'loaded' });
        });

        popupPhotoPreviewImage.addEventListener('error', () => {
          switchPhotoPreviewState({ state: 'error' });
        });

        popupPhotoUrlInput.addEventListener('change', (event) => {
          switchPhotoPreviewState({ state: 'processing' });

          popupPhotoPreviewImage.src = event.target.value;
        });

        popupPhotoPreviewDeleteButton.addEventListener('click', () => {
          popupPhotoUrlInput.value = '';
          popupPhotoPreviewImage.src = '';
        });
      }
    }
  };

  window.addEventListener('DOMContentLoaded', callback);
})();
